/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { isGraphQLError } from '@confluence/network';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { useTrialDetailsTrialDetailsQuery as useTrialDetailsTrialDetailsQuery$data } from './__types__/useTrialDetailsTrialDetailsQuery.ts';

type UseTrialDetailsProps = {
	skip?: boolean;
};

export const useTrialDetails = ({ skip = true }: UseTrialDetailsProps) => {
	const { isLoggedIn } = useSessionData();

	const shouldSkipQuery = skip || !isLoggedIn;
	const {
		data: trialData,
		loading: trialDetailsLoading,
		error: trialDetailsError,
	} = useQuery<useTrialDetailsTrialDetailsQuery$data>(
		gql`
			query useTrialDetailsTrialDetailsQuery {
				license {
					trialEndDate
					billingSourceSystem
				}
			}
		`,
		{
			skip: shouldSkipQuery,
		},
	);

	if (isGraphQLError(trialDetailsError) && !isErrorMarkedAsHandled(trialDetailsError)) {
		markErrorAsHandled(trialDetailsError);
	}

	const trialDaysLeft = getTrialDaysLeftFromTrialEndDate(trialData?.license?.trialEndDate) ?? 0;

	return {
		trialData,
		trialDetailsLoading,
		trialDetailsError,
		isInstanceInTrial: trialDaysLeft > 0,
		trialDaysLeft,
		trialEndDate: trialData?.license?.trialEndDate,
		billingSourceSystem: trialData?.license?.billingSourceSystem,
	};
};
